<script setup>
/**
 * FormDisplayTitle
 * Renders a breadcrumb navigation showing the parent component name and the
 * specific record title. Resolves the display title using the component's nameField.
 */
import {computed} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useFormStore} from '@/stores/form'
import Button from '@/common/Button.vue'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const router = useRouter()
const route = useRoute()

// Stores
const componentsStore = useComponentsStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const tableName = computed(() => formStore.value?.tableName)

const originalData = computed(() => formStore.value?.originalData)

const component = computed(() => {
    return componentsStore.items.find(o => o.tableName === tableName.value)
})

const componentId = computed(() => componentsStore.componentIds[tableName.value])

const nameField = computed(() => componentsStore.nameField(componentId.value))

const title = computed(() => originalData.value ? originalData.value[nameField.value] : '')

const showTitle = computed(() => {
    return tableName.value !== 'account_settings' && title.value
})

// --- Methods ---

const goToListing = () => {
    router.push({
        name: 'list',
        params: {
            component: tableName.value
        },
        // Maintains current query params (e.g., product filters/IDs)
        query: route.query
    })
}
</script>

<template>
    <span class="ms-1 d-inline-flex align-items-center small text-nowrap">
        <Button class="btn-sm lh-1 me-1 p-1" @click="goToListing">
            <small>{{ component?.title }}</small>
        </Button>

        <template v-if="showTitle">
            <i class="bi-chevron-compact-right"/>
            <small class="px-2 text-truncate">{{ title }}</small>
        </template>
    </span>
</template>

<style scoped>
.text-truncate {
    max-width: 200px;
}

@media (min-width: 768px) {
    .text-truncate {
        max-width: inherit;
    }
}
</style>